// Guides — public-facing data layer.
//
// The body content lives in guidesContent.generated.ts (rebuilt from
// docs/content/posts/*-EN.md by scripts/build-guides-data.js). This file
// adds the editorial wrapper: category metadata + lookup helpers.

import { GUIDES, GuideContent } from './guidesContent.generated';

export type GuideCategoryId =
  | 'sexual-health'
  | 'hair-loss'
  | 'weight-management'
  | 'skin-care';

export interface GuideCategory {
  id: GuideCategoryId;
  label: string;          // sentence-case display name
  eyebrow: string;        // small-caps eyebrow text
  tagline: string;        // 1-line italic intro
  pillarHook: string;     // longer paragraph for category hub hero
  shopLink: string;       // matching /shop/<category> for cross-link
}

// Category descriptions are the bridge between the editorial voice (Guides) and
// the catalog. Each tagline mirrors the JMONG-style "단정 hook" of the articles
// themselves so users feel one continuous voice across hub → category → article.
export const GUIDE_CATEGORIES: GuideCategory[] = [
  {
    id: 'sexual-health',
    label: 'Sexual Health',
    eyebrow: 'PDE5 inhibitors • PE • TRT economics',
    tagline: 'Most ED at 30 isn’t psychological.',
    pillarHook: 'Sildenafil, tadalafil, vardenafil, avanafil. Telehealth defaults vs FDA labels. The 30x markup nobody explains. Why your dick is your heart’s smoke alarm.',
    shopLink: '/shop/sexual-health',
  },
  {
    id: 'hair-loss',
    label: 'Hair Loss',
    eyebrow: 'Finasteride • Dutasteride • Minoxidil',
    tagline: 'The side-effect numbers Reddit doesn’t want you to see.',
    pillarHook: 'Finasteride at 1mg vs 5mg. Topical vs oral minoxidil. Why post-finasteride syndrome is real but rarer than you’ve been told. What the trials actually showed at 5 years.',
    shopLink: '/shop/hair-loss',
  },
  {
    id: 'weight-management',
    label: 'Weight & GLP-1',
    eyebrow: 'Semaglutide • Tirzepatide • Compounded vs branded',
    tagline: 'Stop Ozempic and 67% of the weight comes back. Plan for it.',
    pillarHook: 'Wegovy at $1,800 vs LiberaCure-routed at $80. Compounding pharmacy collapse. Why oral semaglutide is the underrated GLP-1. What "maintenance dose" actually means.',
    shopLink: '/shop/weight-management',
  },
  {
    id: 'skin-care',
    label: 'Skin & Acne',
    eyebrow: 'Isotretinoin • Tretinoin • iPledge workarounds',
    tagline: 'iPledge gatekeeps for U.S. dermatology revenue, not safety.',
    pillarHook: 'Isotretinoin without the iPledge maze. Tretinoin strength selection. Why dermatology gatekeeping happens — and where the actual safety data lines are.',
    shopLink: '/shop/skin-care',
  },
];

export function getCategory(id: string): GuideCategory | null {
  return GUIDE_CATEGORIES.find(c => c.id === id) || null;
}

export function getGuide(slug: string): GuideContent | null {
  return GUIDES.find(g => g.slug === slug) || null;
}

export function getGuidesByCategory(categoryId: string): GuideContent[] {
  return GUIDES.filter(g => g.category === categoryId);
}

export function getAllGuides(): GuideContent[] {
  return GUIDES;
}

// "Related" = same category, different slug, capped at N. Falls back to
// other-category guides if the same category doesn't have enough.
export function getRelatedGuides(slug: string, limit = 3): GuideContent[] {
  const current = getGuide(slug);
  if (!current) return [];
  const sameCategory = GUIDES.filter(g => g.category === current.category && g.slug !== slug);
  if (sameCategory.length >= limit) return sameCategory.slice(0, limit);
  const others = GUIDES.filter(g => g.category !== current.category && g.slug !== slug);
  return [...sameCategory, ...others].slice(0, limit);
}

export type { GuideContent };
